import { Link } from 'react-router-dom';
import SEO from '../../components/base/SEO';

export default function ServiciosPage() {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "VACIADOS MARIUS",
    "@id": `${import.meta.env.VITE_SITE_URL || 'https://vaciado-pisos-barcelona.vaciadosmarius.com.es'}/#organization`,
    "url": import.meta.env.VITE_SITE_URL || "https://vaciado-pisos-barcelona.vaciadosmarius.com.es/",
    "telephone": "+34936940041",
    "email": "vaciadosmarius@gmail.com",
    "sameAs": "https://share.google/FizsQeezE7RkuyEkt"
  };

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Vaciado completo de pisos",
        "description": "Vaciado integral de viviendas incluyendo muebles, enseres, desmontaje y gestión de residuos"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Recogida selectiva de muebles",
        "description": "Retirada de muebles y objetos específicos sin vaciado completo"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Vaciado por herencia",
        "description": "Servicio especializado en vaciado de pisos tras fallecimientos con máxima discreción"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Vaciado para reformas",
        "description": "Vaciado rápido de viviendas para inicio de obras o fin de contrato"
      },
      {
        "@type": "ListItem",
        "position": 5,
        "name": "Compra de muebles de diseño y antigüedades",
        "description": "Compra de muebles de diseño firmados y antigüedades de más de 100 años"
      },
      {
        "@type": "ListItem",
        "position": 6,
        "name": "Limpieza final opcional",
        "description": "Limpieza profunda tras el vaciado para entrega de llaves"
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": import.meta.env.VITE_SITE_URL || "https://vaciado-pisos-barcelona.vaciadosmarius.com.es/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Servicios",
        "item": `${import.meta.env.VITE_SITE_URL || 'https://vaciado-pisos-barcelona.vaciadosmarius.com.es'}/servicios`
      }
    ]
  };

  return (
    <>
      <SEO
        title="Servicios de Vaciado de Pisos Barcelona | Recogida Muebles | VACIADOS MARIUS"
        description="Vaciado completo, recogida selectiva, herencias, reformas, compra de antigüedades y limpieza final. Servicios profesionales en Barcelona. Presupuesto gratis."
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://vaciado-pisos-barcelona.vaciadosmarius.com.es'}/servicios`}
        keywords="servicios vaciado pisos Barcelona, recogida muebles, vaciado herencias, vaciado reformas, compra antigüedades Barcelona"
        geoRegion="ES-CT"
        geoPlacename="Barcelona"
        geoPosition="41.3851;2.1734"
        jsonLd={[localBusinessSchema, servicesSchema, breadcrumbSchema]}
      />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-sm text-gray-600 mb-8">
              <Link to="/" className="hover:text-teal-600 cursor-pointer">Inicio</Link>
              <span className="mx-2">&gt;</span>
              <span className="text-gray-900">Servicios</span>
            </nav>

            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Servicios de vaciado y recogida en Barcelona
              </h1>
              <p className="text-xl text-gray-600">
                Soluciones completas para cada situación
              </p>
            </div>
          </div>
        </section>

        {/* Service 1: Vaciado Completo */}
        <section id="vaciado-completo" className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">
                  <span className="text-gray-900">Vaciado completo de </span>
                  <span className="text-teal-600">pisos</span>
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nuestro servicio de <strong>vaciado completo de pisos en Barcelona</strong> incluye la retirada de todo el mobiliario, electrodomésticos, enseres y objetos del inmueble. Nos encargamos del desmontaje básico cuando es necesario y de la gestión responsable de todos los residuos según normativa y gestores autorizados cuando aplique.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  El <strong>vaciado puede ser gratuito</strong> si los muebles y objetos tienen valor suficiente para compensar el coste del trabajo. Evaluamos cada caso individualmente y te damos una respuesta clara en 24-48 horas. Conoce más sobre <a href="/empresa#como-trabajamos" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">nuestro método de trabajo</a>.
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Un <strong>vaciado de vivienda</strong> estándar (60-80m²) se completa en 3-6 horas. Pisos más grandes o con mucho contenido pueden requerir 1-2 días.
                </p>

                <div className="bg-gray-50 rounded-xl p-6 mb-8">
                  <h3 className="font-bold text-gray-900 mb-4">Incluye:</h3>
                  <ul className="space-y-3">
                    {[
                      'Retirada de todo el mobiliario',
                      'Desmontaje básico de muebles',
                      'Protección de escaleras y ascensor',
                      'Carga y transporte profesional',
                      'Gestión de residuos según normativa'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  ¿Necesitas también <a href="#limpieza" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">limpieza final</a>?
                </p>
              </div>

              <div>
                <img
                  src="https://public.readdy.ai/ai/img_res/fe9cb674a8437d3e29230d2b1af0e2f8.jpg"
                  alt="Vaciado completo de pisos"
                  className="w-full h-[600px] object-cover object-top rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Service 2: Recogida Selectiva */}
        <section id="recogida-muebles" className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <img
                  src="https://readdy.ai/api/search-image?query=Two%20movers%20carefully%20carrying%20single%20large%20sofa%20down%20apartment%20stairs%2C%20selective%20furniture%20removal%20service%2C%20focused%20item%20pickup%2C%20professional%20handling%20of%20individual%20furniture%20piece%2C%20Barcelona%20building%20stairwell%20with%20protective%20padding%20on%20walls&width=800&height=900&seq=selective-pickup-001&orientation=portrait"
                  alt="Recogida selectiva de muebles"
                  className="w-full h-[600px] object-cover object-top rounded-2xl shadow-lg"
                />
              </div>

              <div className="order-1 lg:order-2">
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Recogida selectiva de muebles y enseres
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Si no necesitas vaciar todo el piso, ofrecemos <strong>recogida selectiva de muebles en Barcelona</strong>. Ideal para retirar objetos específicos, muebles que sobran tras una mudanza, o enseres que ya no necesitas.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Este servicio es perfecto para situaciones como: sofás viejos que quieres reemplazar, electrodomésticos que no funcionan, muebles de jardín, colchones, o cualquier objeto voluminoso que necesites retirar.
                </p>
                <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-8">
                  <p className="text-gray-800 font-semibold mb-2">
                    <i className="ri-information-line text-teal-600 mr-2"></i>
                    Importante sobre compra de muebles
                  </p>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Si son muebles de diseño firmados (Eames, Thonet, Cassina, etc.) o antigüedades de más de 100 años, podemos comprarlos. Envíanos fotos, marca, medidas y estado por WhatsApp para valoración. Lee más sobre <a href="#compra-muebles" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">qué muebles compramos</a>.
                  </p>
                </div>

                <a
                  href="https://wa.me/34633759649"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-8 py-4 bg-green-600 text-white rounded-full font-semibold hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-fill text-xl"></i>
                  <span>SOLICITAR RECOGIDA</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CTA 1 */}
        <section className="bg-teal-600 py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-white text-xl mb-6">¿Hablamos de tu caso?</p>
            <a
              href="tel:+34936940041"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-teal-600 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              <span>LLAMAR 936 94 00 41</span>
            </a>
          </div>
        </section>

        {/* Service 3: Herencias */}
        <section id="herencias" className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Vaciado de pisos por herencia o fallecimiento
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  El <strong>vaciado de pisos por herencia</strong> requiere sensibilidad, discreción y respeto. Entendemos que estás pasando por un momento difícil y queremos ayudarte a gestionar esta tarea de la forma más cuidadosa posible.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nuestro equipo tiene experiencia en situaciones delicadas. Te ayudamos a identificar objetos con valor sentimental o económico antes de proceder. Coordinamos con todos los familiares involucrados y respetamos los tiempos que necesites.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Tratamos los objetos personales, documentos y fotografías con máxima delicadeza. Los separamos y te consultamos antes de gestionar nada. Muchas veces hay muebles antiguos o de diseño que pueden tener valor y que podemos comprar o que compensan el coste del vaciado.
                </p>
                <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-8">
                  <p className="text-gray-800 font-semibold mb-2">
                    <i className="ri-lightbulb-line text-amber-600 mr-2"></i>
                    Recomendación importante
                  </p>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    No tires nada antes de consultarnos. Podemos ayudarte a identificar objetos con valor sentimental o económico que quizás no reconozcas a simple vista.
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  Lee más sobre <Link to="/empresa" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">nuestro compromiso</Link> con las familias y nuestra <a href="/faq" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">política de transparencia</a>.
                </p>
              </div>

              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Compassionate%20team%20member%20carefully%20packing%20photo%20albums%20and%20personal%20belongings%20with%20white%20gloves%2C%20respectful%20handling%20of%20sentimental%20items%20during%20estate%20clearance%2C%20gentle%20and%20dignified%20approach%2C%20soft%20lighting%2C%20professional%20care%20for%20family%20memories%20and%20documents&width=800&height=900&seq=heritage-care-001&orientation=portrait"
                  alt="Vaciado por herencia"
                  className="w-full h-[600px] object-cover object-top rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Service 4: Reformas */}
        <section id="reformas" className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <img
                  src="https://readdy.ai/api/search-image?query=Empty%20apartment%20ready%20for%20renovation%20with%20construction%20tools%20and%20materials%20visible%2C%20cleared%20space%20prepared%20for%20contractors%20to%20begin%20work%2C%20bright%20vacant%20rooms%20with%20clean%20floors%2C%20professional%20clearance%20before%20remodeling%2C%20Barcelona%20apartment%20renovation%20preparation&width=800&height=900&seq=renovation-ready-001&orientation=portrait"
                  alt="Vaciado para reformas"
                  className="w-full h-[600px] object-cover object-top rounded-2xl shadow-lg"
                />
              </div>

              <div className="order-1 lg:order-2">
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Vaciado para reformas y fin de contrato
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Cuando necesitas iniciar una <strong>reforma integral</strong> o entregar un piso al finalizar un contrato de alquiler, el tiempo es crucial. Nuestro servicio de <strong>vaciado para reformas en Barcelona</strong> está diseñado para cumplir plazos ajustados.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Coordinamos con contratistas, propietarios e inquilinos para que todo fluya sin problemas. Podemos programar el vaciado en el momento exacto que necesites, incluso con urgencia de 1-2 días si es necesario.
                </p>

                <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4">Situaciones típicas:</h3>
                  <ul className="space-y-2">
                    {[
                      'Fin de contrato de alquiler con fecha límite',
                      'Inicio de reforma integral',
                      'Preparación de piso para venta',
                      'Cambio de inquilinos urgente',
                      'Liberación de espacio para obras'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <i className="ri-arrow-right-s-line text-teal-600 text-xl flex-shrink-0"></i>
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-gray-700 leading-relaxed mb-6">
                  Si necesitas entregar el piso limpio, ofrecemos <a href="#limpieza" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">limpieza final opcional</a> para que quede listo para la entrega de llaves. También disponemos de <a href="#pintura-interior" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">servicio de pintura interior</a>.
                </p>

                <a
                  href="https://wa.me/34633759649"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-8 py-4 bg-red-600 text-white rounded-full font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-time-line text-xl"></i>
                  <span>PRESUPUESTO URGENTE</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CTA 2 */}
        <section className="bg-green-600 py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-white text-xl mb-6">Envíanos fotos por WhatsApp</p>
            <a
              href="https://wa.me/34633759649"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-green-600 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-fill text-xl"></i>
              <span>WHATSAPP 633 759 649</span>
            </a>
          </div>
        </section>

        {/* Service 5: Compra de Muebles */}
        <section id="compra-muebles" className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">
                  <span className="text-gray-900">Compramos muebles de diseño y </span>
                  <span className="text-teal-600">antigüedades (+100 años)</span>
                </h2>

                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-8">
                  <h3 className="font-bold text-red-900 mb-3 flex items-center">
                    <i className="ri-error-warning-line text-red-600 text-xl mr-2"></i>
                    IMPORTANTE
                  </h3>
                  <p className="text-gray-800 leading-relaxed mb-4">
                    Solo compramos <strong>muebles de diseño firmados</strong> (Eames, Thonet, Cassina, Knoll, Vitra, etc.) o <strong>antigüedades auténticas de más de 100 años</strong> con valor histórico.
                  </p>
                  <p className="text-gray-800 leading-relaxed">
                    <strong>NO compramos muebles comunes</strong> de Ikea, Conforama, Maisons du Monde o similares. Estos se pueden retirar dentro del <a href="#vaciado-completo" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">servicio de vaciado</a> con presupuesto aparte.
                  </p>
                </div>

                <p className="text-gray-700 leading-relaxed mb-6">
                  Si crees que tienes muebles con valor, envíanos fotos por WhatsApp. Necesitamos ver:
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    'Fotos desde varios ángulos',
                    'Marca o etiquetas del fabricante',
                    'Medidas aproximadas (alto, ancho, fondo)',
                    'Estado general (desgaste, daños, funcionamiento)',
                    'Cualquier detalle que indique antigüedad o diseño'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <i className="ri-camera-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://wa.me/34633759649"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-8 py-4 bg-green-600 text-white rounded-full font-semibold hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-fill text-xl"></i>
                  <span>ENVIAR FOTOS PARA VALORACIÓN</span>
                </a>
              </div>

              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Extreme%20close-up%20of%20authentic%20designer%20furniture%20label%20showing%20Eames%20or%20Thonet%20brand%20mark%2C%20detailed%20view%20of%20manufacturer%20tag%20on%20vintage%20chair%2C%20professional%20furniture%20authentication%2C%20high-quality%20craftsmanship%20details%2C%20designer%20furniture%20verification&width=800&height=900&seq=designer-label-001&orientation=portrait"
                  alt="Compra de muebles de diseño"
                  className="w-full h-[600px] object-cover object-top rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Service 6: Limpieza Final */}
        <section id="limpieza" className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <img
                  src="https://readdy.ai/api/search-image?query=Spotless%20clean%20empty%20apartment%20with%20shiny%20polished%20floors%20reflecting%20sunlight%2C%20pristine%20white%20walls%20and%20sparkling%20windows%2C%20professional%20deep%20cleaning%20results%2C%20immaculate%20vacant%20Barcelona%20apartment%20ready%20for%20handover%2C%20gleaming%20surfaces%20and%20perfect%20cleanliness&width=800&height=900&seq=clean-apartment-001&orientation=portrait"
                  alt="Limpieza final"
                  className="w-full h-[600px] object-cover object-top rounded-2xl shadow-lg"
                />
              </div>

              <div className="order-1 lg:order-2">
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Limpieza final opcional
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  La <strong>limpieza final</strong> es un servicio adicional que ofrecemos tras el vaciado. Después de retirar todos los muebles y enseres, el piso queda libre pero puede necesitar una limpieza profunda para la entrega al propietario, venta o nuevo alquiler.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nuestro servicio de limpieza incluye: barrido y fregado de suelos, limpieza de ventanas, armarios empotrados, baños, cocina, eliminación de polvo y suciedad acumulada. Dejamos el piso en perfectas condiciones.
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Este servicio es especialmente útil cuando necesitas entregar llaves a un propietario, preparar el piso para fotografías de venta, o simplemente quieres que quede impecable tras el vaciado.
                </p>

                <div className="bg-teal-50 rounded-xl p-6 mb-8">
                  <p className="text-gray-800 font-semibold mb-2">
                    <i className="ri-information-line text-teal-600 mr-2"></i>
                    Combínalo con el vaciado
                  </p>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Si contratas la limpieza junto con el <a href="#vaciado-completo" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">vaciado completo</a>, coordinamos todo en el mismo día para mayor comodidad. También puedes añadir <a href="#pintura-interior" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">pintura interior</a> para un servicio integral.
                  </p>
                </div>

                <p className="text-sm text-gray-600">
                  Consúltanos precio según los metros cuadrados del piso. <a href="/contacto" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">Solicita presupuesto sin compromiso</a>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Service 7: Pintura Interior */}
        <section id="pintura-interior" className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">
                  <span className="text-gray-900">Pintura interior para </span>
                  <span className="text-teal-600">pisos vaciados</span>
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Después del vaciado, muchos pisos necesitan una <strong>renovación de pintura interior</strong> para quedar listos para venta, alquiler o entrega. Ofrecemos servicio de pintura profesional en Barcelona para dejar tu vivienda impecable.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nuestro servicio de <strong>pintura interior</strong> incluye preparación de superficies, protección de suelos y elementos fijos, aplicación de pintura de calidad en paredes y techos, y limpieza final. Trabajamos con pinturas de primeras marcas y acabados duraderos.
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Ideal para pisos que necesitan renovación tras el vaciado, herencias, reformas o cambio de inquilinos. Coordinamos el servicio de pintura después del vaciado y limpieza para optimizar tiempos.
                </p>

                <div className="bg-gray-50 rounded-xl p-6 mb-8">
                  <h3 className="font-bold text-gray-900 mb-4">El servicio incluye:</h3>
                  <ul className="space-y-3">
                    {[
                      'Preparación y lijado de superficies',
                      'Protección de suelos y elementos',
                      'Pintura de paredes y techos',
                      'Pinturas de calidad profesional',
                      'Limpieza final del espacio'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-8">
                  <p className="text-gray-800 font-semibold mb-2">
                    <i className="ri-paint-brush-line text-teal-600 mr-2"></i>
                    Servicio completo
                  </p>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Combina <a href="#vaciado-completo" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">vaciado</a> + <a href="#limpieza" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">limpieza</a> + pintura en un solo proceso coordinado. Te ahorramos tiempo y gestiones.
                  </p>
                </div>

                <a
                  href="https://wa.me/34633759649"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-8 py-4 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-fill text-xl"></i>
                  <span>PRESUPUESTO PINTURA</span>
                </a>
              </div>

              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20painter%20applying%20fresh%20white%20paint%20to%20apartment%20walls%20with%20roller%2C%20interior%20painting%20service%20in%20Barcelona%2C%20smooth%20wall%20coverage%20with%20quality%20paint%2C%20worker%20in%20clean%20uniform%20painting%20empty%20renovated%20room%2C%20bright%20natural%20lighting%20showing%20perfect%20finish&width=800&height=900&seq=interior-painting-001&orientation=portrait"
                  alt="Pintura interior de pisos"
                  className="w-full h-[600px] object-cover object-top rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Checklist Section */}
        <section className="bg-white py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-teal-50 rounded-2xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Qué preparar antes del vaciado
              </h2>

              <div className="space-y-4 mb-8">
                {[
                  'Identifica objetos de valor sentimental que quieras conservar',
                  'Envíanos fotos de muebles que creas que pueden tener valor',
                  'Avisa a la comunidad de vecinos si es necesario',
                  'Reserva el ascensor si tu edificio lo requiere',
                  'Ten a mano las llaves y accesos',
                  'Consulta si necesitas permisos municipales (poco común)'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4 bg-white rounded-lg p-4">
                    <div className="w-6 h-6 flex items-center justify-center border-2 border-teal-600 rounded flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-center text-gray-700 font-semibold">
                ¿Dudas? Llámanos y te guiamos en todo el proceso. También puedes consultar nuestras <a href="/faq" className="text-teal-600 hover:text-teal-700 cursor-pointer">preguntas frecuentes</a>.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-gray-50 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              ¿Qué servicio necesitas?
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <a
                href="tel:+34936940041"
                className="flex items-center space-x-2 px-8 py-4 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill text-xl"></i>
                <span>LLAMAR</span>
              </a>
              <a
                href="https://wa.me/34633759649"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-8 py-4 bg-green-600 text-white rounded-full font-semibold hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-fill text-xl"></i>
                <span>WHATSAPP</span>
              </a>
              <Link
                to="/contacto"
                className="flex items-center space-x-2 px-8 py-4 bg-gray-900 text-white rounded-full font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line text-xl"></i>
                <span>IR A CONTACTO</span>
              </Link>
            </div>
            <p className="text-gray-600">
              O escríbenos a vaciadosmarius@gmail.com con los detalles. Conoce más sobre <Link to="/empresa" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">nuestra empresa</Link>.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
